
import React, { useRef } from 'react';
import { Attachment } from '../types';
import { Button } from './ui/Button';
import { XMarkIcon } from './icons/XMarkIcon';
import { PaperClipIcon } from './icons/PaperClipIcon';

interface AttachmentInputProps {
  label?: string;
  attachments: Attachment[];
  onChange: (attachments: Attachment[]) => void;
  containerClassName?: string;
}

const MAX_FILES = 6;
const MAX_INDIVIDUAL_FILE_SIZE_MB = 5; 
const MAX_TOTAL_FILES_SIZE_MB = 20; 
const MAX_INDIVIDUAL_FILE_SIZE_BYTES = MAX_INDIVIDUAL_FILE_SIZE_MB * 1024 * 1024;
const MAX_TOTAL_FILES_SIZE_BYTES = MAX_TOTAL_FILES_SIZE_MB * 1024 * 1024;

export const AttachmentInput: React.FC<AttachmentInputProps> = ({ label, attachments = [], onChange, containerClassName = '' }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    // 1. Check total number of files
    if (attachments.length + files.length > MAX_FILES) {
      alert(`Você pode anexar no máximo ${MAX_FILES} arquivos.`);
      if (fileInputRef.current) fileInputRef.current.value = ""; // Reset input
      return;
    }

    // 2. Check total combined file size
    const currentAttachmentsSize = attachments.reduce((sum, att) => sum + (atob(att.url.split(',')[1] || '').length), 0);
    const newFilesSize = Array.from(files).reduce((sum, file) => sum + file.size, 0);

    if (currentAttachmentsSize + newFilesSize > MAX_TOTAL_FILES_SIZE_BYTES) {
        alert(`O tamanho total de todos os anexos não pode exceder ${MAX_TOTAL_FILES_SIZE_MB}MB.`);
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
    }
    
    const oversizedFiles: string[] = [];
    const validFiles: File[] = [];

    // 3. Check individual file sizes
    Array.from(files).forEach(file => {
      if (file.size > MAX_INDIVIDUAL_FILE_SIZE_BYTES) {
        oversizedFiles.push(file.name);
      } else {
        validFiles.push(file);
      }
    });

    if (oversizedFiles.length > 0) {
      alert(`Os seguintes arquivos excedem o limite de ${MAX_INDIVIDUAL_FILE_SIZE_MB.toFixed(2)}MB por arquivo e não serão adicionados:\n\n- ${oversizedFiles.join('\n- ')}`);
    }
    
    if (validFiles.length === 0) {
        if (fileInputRef.current) { fileInputRef.current.value = ""; }
        return; // No valid files to process
    }

    // 4. Process valid files
    const filePromises: Promise<Attachment>[] = validFiles.map(file => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const newAttachment: Attachment = {
            id: `${file.name}-${Date.now()}`,
            name: file.name,
            url: reader.result as string,
            type: file.type,
          };
          resolve(newAttachment);
        };
        reader.readAsDataURL(file);
      });
    });

    Promise.all(filePromises).then(newlyCreatedAttachments => {
      onChange([...attachments, ...newlyCreatedAttachments]);
    });

    // Reset file input to allow selecting the same file again after removing it
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeAttachment = (id: string) => {
    onChange(attachments.filter(att => att.id !== id));
  };

  return (
    <div className={containerClassName}>
      {label && <label className="block text-sm font-medium text-dark-text-secondary mb-1">{label}</label>}
      <div className="flex flex-col space-y-3">
        {/* File Input Button */}
        <div>
          <input
            type="file"
            multiple
            accept="image/jpeg,image/png,image/gif,image/webp,.pdf"
            onChange={handleFileChange}
            ref={fileInputRef}
            className="hidden"
            id="attachment-input"
            disabled={attachments.length >= MAX_FILES}
          />
          <Button type="button" variant="secondary" onClick={() => fileInputRef.current?.click()} disabled={attachments.length >= MAX_FILES} leftIcon={<PaperClipIcon className="text-xl" />}>
            Selecionar Arquivos
          </Button>
          {attachments.length >= MAX_FILES && <p className="text-xs text-yellow-500 mt-1">Limite de {MAX_FILES} anexos atingido.</p>}
        </div>

        {/* Attachments Preview */}
        {attachments.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {attachments.map(att => (
              <div key={att.id} className="relative group bg-dark-surface rounded-md border border-dark-tertiary aspect-square flex items-center justify-center p-1">
                {att.type.startsWith('image/') ? (
                  <img src={att.url} alt={att.name} className="max-w-full max-h-full object-contain" />
                ) : (
                  <div className="flex flex-col items-center text-center">
                    <PaperClipIcon className="text-4xl text-dark-text-secondary" />
                    <p className="text-xs text-dark-text-secondary truncate w-full px-1" title={att.name}>{att.name}</p>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => removeAttachment(att.id)}
                  className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label={`Remover ${att.name}`}
                >
                  <XMarkIcon className="text-base" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
